import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../services/AuthContext';
import Autocomplete from 'react-google-autocomplete';

const RegisterPage = () => {
  const [searchParams] = useSearchParams();
  const refCode = searchParams.get('ref') || '';

  const [userData, setUserData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    role: 'client',
    city: '',
    language: 'English',
    dob: '',
    referralCode: refCode,
    hasNoCode: false,
  });
  const [step, setStep] = useState(1);
  const [surveyData, setSurveyData] = useState({
    goals: '',
    goalsCustom: '',
    preferredStyle: '',
    preferredStyleCustom: '',
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const goalOptions = [
    'Career advancement',
    'Emotional intelligence',
    'Work-life balance',
    'Leadership skills',
    'Stress & Anxiety',
    'Other'
  ];

  const styleOptions = [
    'Structured & Goal-oriented',
    'Empathetic & Listening',
    'Direct & Challenging',
    'Motivational & Inspiring',
    'Other'
  ];

  const { register, user } = useAuth();
  const navigate = useNavigate();

  // Redirect if logged in
  useEffect(() => {
    if (user) {
      if (user.role === 'coach') {
        navigate('/coach/dashboard');
      } else if (user.role === 'client') {
        navigate('/user/coaches');
      } else {
        navigate('/dashboard');
      }
    }
  }, [user, navigate]);

  const calculateAge = (dobString) => {
    const today = new Date();
    const birthDate = new Date(dobString);
    let age = today.getFullYear() - birthDate.getFullYear();
    const m = today.getMonth() - birthDate.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (step === 1) {
      if (userData.password !== userData.confirmPassword) {
        setError('Passwords do not match');
        return;
      }

      if (!userData.dob) {
        setError('Date of birth is required');
        return;
      }

      if (calculateAge(userData.dob) < 18) {
        setError('You must be at least 18 years old to register.');
        return;
      }

      if (userData.role === 'client') {
        setStep(2);
        return;
      }
      
      // If coach, proceed with registration
      await handleFinalRegister();
    } else {
      // Final submit from step 2
      if (!surveyData.goals || (surveyData.goals === 'Other' && !surveyData.goalsCustom)) {
        setError('Please select your goals');
        return;
      }
      if (!surveyData.preferredStyle || (surveyData.preferredStyle === 'Other' && !surveyData.preferredStyleCustom)) {
        setError('Please select your preferred coaching style');
        return;
      }
      await handleFinalRegister();
    }
  };

  const handleFinalRegister = async () => {
    setLoading(true);
    const { confirmPassword, ...registrationData } = userData;
    
    // Add survey data to metadata if it's a client
    if (userData.role === 'client') {
      registrationData.metadata = {
        survey: {
          goals: surveyData.goals === 'Other' ? surveyData.goalsCustom : surveyData.goals,
          preferredStyle: surveyData.preferredStyle === 'Other' ? surveyData.preferredStyleCustom : surveyData.preferredStyle
        }
      };
    }

    const result = await register(registrationData);

    if (result.success) {
      const storedUser = JSON.parse(localStorage.getItem('user'));
      
      if (storedUser.role === 'coach') {
        navigate('/coach/dashboard');
      } else if (storedUser.role === 'client') {
        navigate('/user/coaches');
      } else {
        navigate('/dashboard');
      }
    } else {
      setError(result.error);
    }

    setLoading(false);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setUserData({
      ...userData,
      [name]: type === 'checkbox' ? checked : value,
    });
  };

  return (
    <div className="min-h-screen bg-[var(--bg-main)] flex flex-col justify-center py-12 sm:px-6 lg:px-8 font-display">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <h2 className="mt-6 text-center text-3xl font-extrabold text-[var(--text-primary)]">
          {step === 1 ? 'Create Account' : 'Tell us about yourself'}
        </h2>
        <p className="mt-2 text-center text-sm text-[var(--text-secondary)]">
          {step === 1 ? (
            <>
              Or{' '}
              <Link
                to="/login"
                className="font-medium text-tangerine hover:text-tangerine/80"
              >
                sign in to existing account
              </Link>
            </>
          ) : (
            'Step 2 of 2: Quick survey'
          )}
        </p>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-2xl">
        <div className="bg-[var(--surface-white)] py-8 px-4 shadow-xl shadow-black/5 border border-[var(--border-light)] sm:rounded-3xl sm:px-10">
          <form className="space-y-6" onSubmit={handleSubmit}>
            {error && (
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-600 dark:text-red-400 px-4 py-3 rounded">
                {error}
              </div>
            )}

            {step === 1 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-[var(--text-secondary)]">
                    Name
                  </label>
                  <div className="mt-1">
                    <input
                      id="name"
                      name="name"
                      type="text"
                      required
                      className="appearance-none block w-full px-3 py-2 border border-[var(--border-light)] rounded-md placeholder-gray-400 focus:outline-none focus:ring-tangerine focus:border-tangerine dark:bg-[var(--off-white)] dark:text-[var(--text-primary)]"
                      value={userData.name}
                      onChange={handleChange}
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-[var(--text-secondary)]">
                    Email
                  </label>
                  <div className="mt-1">
                    <input
                      id="email"
                      name="email"
                      type="email"
                      autoComplete="email"
                      required
                      className="appearance-none block w-full px-3 py-2 border border-[var(--border-light)] rounded-md placeholder-gray-400 focus:outline-none focus:ring-tangerine focus:border-tangerine dark:bg-[var(--off-white)] dark:text-[var(--text-primary)]"
                      value={userData.email}
                      onChange={handleChange}
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="role" className="block text-sm font-medium text-[var(--text-secondary)]">
                    I want to register as a
                  </label>
                  <div className="mt-1">
                    <select
                      id="role"
                      name="role"
                      className="appearance-none block w-full px-3 py-2 border border-[var(--border-light)] rounded-md placeholder-gray-400 focus:outline-none focus:ring-tangerine focus:border-tangerine dark:bg-[var(--off-white)] dark:text-[var(--text-primary)]"
                      value={userData.role}
                      onChange={handleChange}
                    >
                      <option value="client">Client</option>
                      <option value="coach">Coach</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label htmlFor="dob" className="block text-sm font-medium text-[var(--text-secondary)]">
                    Date of Birth
                  </label>
                  <div className="mt-1">
                    <input
                      id="dob"
                      name="dob"
                      type="date"
                      required
                      className="appearance-none block w-full px-3 py-2 border border-[var(--border-light)] rounded-md placeholder-gray-400 focus:outline-none focus:ring-tangerine focus:border-tangerine text-[var(--text-primary)] dark:bg-[var(--off-white)]"
                      value={userData.dob}
                      onChange={handleChange}
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="city" className="block text-sm font-medium text-[var(--text-secondary)]">
                    City
                  </label>
                  <div className="mt-1">
                    <Autocomplete
                      id="city"
                      apiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY}
                      onPlaceSelected={(place) => {
                        setUserData(prev => ({
                          ...prev,
                          city: place.formatted_address || place.name || ''
                        }));
                      }}
                      options={{
                        types: ['(cities)'],
                      }}
                      name="city"
                      required
                      className="appearance-none block w-full px-3 py-2 border border-[var(--border-light)] rounded-md placeholder-gray-400 focus:outline-none focus:ring-tangerine focus:border-tangerine dark:bg-[var(--off-white)] dark:text-[var(--text-primary)]"
                      value={userData.city}
                      onChange={handleChange}
                      placeholder="Start typing your city..."
                    />
                    {!process.env.REACT_APP_GOOGLE_MAPS_API_KEY && (
                      <p className="text-xs text-amber-500 mt-1">Google Maps API Key is missing in .env</p>
                    )}
                  </div>
                </div>

                <div>
                  <label htmlFor="language" className="block text-sm font-medium text-[var(--text-secondary)]">
                    Primary Language
                  </label>
                  <div className="mt-1">
                    <select
                      id="language"
                      name="language"
                      required
                      className="appearance-none block w-full px-3 py-2 border border-[var(--border-light)] rounded-md placeholder-gray-400 focus:outline-none focus:ring-tangerine focus:border-tangerine dark:bg-[var(--off-white)] dark:text-[var(--text-primary)]"
                      value={userData.language}
                      onChange={handleChange}
                    >
                      <option value="English">English</option>
                      <option value="Spanish">Spanish</option>
                      <option value="French">French</option>
                      <option value="German">German</option>
                      <option value="Chinese">Chinese</option>
                      <option value="Japanese">Japanese</option>
                      <option value="Russian">Russian</option>
                      <option value="Arabic">Arabic</option>
                      <option value="Hindi">Hindi</option>
                      <option value="Portuguese">Portuguese</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>
                </div>

                {userData.role !== 'coach' && (
                  <div className="md:col-span-2 border-t border-[var(--border-light)] pt-6">
                    <label htmlFor="referralCode" className="block text-sm font-medium text-[var(--text-secondary)]">
                      Referral Code
                    </label>
                    <div className="mt-1">
                      <input
                        id="referralCode"
                        name="referralCode"
                        type="text"
                        disabled={userData.hasNoCode || !!refCode}
                        className="appearance-none block w-full px-3 py-2 border border-[var(--border-light)] rounded-md placeholder-gray-400 focus:outline-none focus:ring-tangerine focus:border-tangerine disabled:bg-[var(--off-white)] disabled:text-[var(--text-secondary)] dark:bg-[var(--off-white)] dark:text-[var(--text-primary)]"
                        value={userData.referralCode}
                        onChange={handleChange}
                        placeholder={refCode ? "" : (userData.hasNoCode ? "Added to waitlist" : "Enter referral code (if any)")}
                      />
                    </div>
                    {!refCode && (
                      <div className="mt-2 flex items-center">
                        <input
                          id="hasNoCode"
                          name="hasNoCode"
                          type="checkbox"
                          className="h-4 w-4 text-tangerine focus:ring-tangerine border-[var(--border-light)] rounded dark:bg-[var(--off-white)]"
                          checked={userData.hasNoCode}
                          onChange={handleChange}
                        />
                        <label htmlFor="hasNoCode" className="ml-2 block text-sm text-[var(--text-secondary)]">
                          I don't have a code (Add me to the waitlist)
                        </label>
                      </div>
                    )}
                  </div>
                )}

                <div>
                  <label htmlFor="password" className="block text-sm font-medium text-[var(--text-secondary)]">
                    Password
                  </label>
                  <div className="mt-1">
                    <input
                      id="password"
                      name="password"
                      type="password"
                      autoComplete="new-password"
                      required
                      className="appearance-none block w-full px-3 py-2 border border-[var(--border-light)] rounded-md placeholder-gray-400 focus:outline-none focus:ring-tangerine focus:border-tangerine dark:bg-[var(--off-white)] dark:text-[var(--text-primary)]"
                      value={userData.password}
                      onChange={handleChange}
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="confirmPassword" className="block text-sm font-medium text-[var(--text-secondary)]">
                    Confirm Password
                  </label>
                  <div className="mt-1">
                    <input
                      id="confirmPassword"
                      name="confirmPassword"
                      type="password"
                      autoComplete="new-password"
                      required
                      className="appearance-none block w-full px-3 py-2 border border-[var(--border-light)] rounded-md placeholder-gray-400 focus:outline-none focus:ring-tangerine focus:border-tangerine dark:bg-[var(--off-white)] dark:text-[var(--text-primary)]"
                      value={userData.confirmPassword}
                      onChange={handleChange}
                    />
                  </div>
                </div>
              </div>
            ) : (
              <div className="space-y-8">
                <div>
                  <label className="block text-sm font-semibold text-[var(--text-secondary)] mb-3">
                    What are your goals or purpose for joining?
                  </label>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {goalOptions.map((option) => (
                      <button
                        key={option}
                        type="button"
                        onClick={() => setSurveyData(prev => ({ ...prev, goals: option }))}
                        className={`px-4 py-3 text-sm rounded-xl border text-left transition-all duration-200 ${
                          surveyData.goals === option
                            ? 'bg-tangerine/10 border-tangerine text-tangerine font-semibold shadow-sm'
                            : 'bg-[var(--surface-white)] border-[var(--border-light)] text-[var(--text-primary)] hover:border-tangerine/50 hover:bg-tangerine/5'
                        }`}
                      >
                        {option}
                      </button>
                    ))}
                  </div>
                  {surveyData.goals === 'Other' && (
                    <div className="mt-3 animate-in fade-in slide-in-from-top-2">
                      <input
                        type="text"
                        placeholder="Please specify your goals..."
                        className="appearance-none block w-full px-3 py-2 border border-tangerine/30 rounded-md placeholder-gray-400 focus:outline-none focus:ring-tangerine focus:border-tangerine dark:bg-[var(--off-white)] dark:text-[var(--text-primary)]"
                        value={surveyData.goalsCustom}
                        onChange={(e) => setSurveyData(prev => ({ ...prev, goalsCustom: e.target.value }))}
                      />
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-semibold text-[var(--text-secondary)] mb-3">
                    Preferred coaching style
                  </label>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {styleOptions.map((option) => (
                      <button
                        key={option}
                        type="button"
                        onClick={() => setSurveyData(prev => ({ ...prev, preferredStyle: option }))}
                        className={`px-4 py-3 text-sm rounded-xl border text-left transition-all duration-200 ${
                          surveyData.preferredStyle === option
                            ? 'bg-tangerine/10 border-tangerine text-tangerine font-semibold shadow-sm'
                            : 'bg-[var(--surface-white)] border-[var(--border-light)] text-[var(--text-primary)] hover:border-tangerine/50 hover:bg-tangerine/5'
                        }`}
                      >
                        {option}
                      </button>
                    ))}
                  </div>
                  {surveyData.preferredStyle === 'Other' && (
                    <div className="mt-3 animate-in fade-in slide-in-from-top-2">
                      <input
                        type="text"
                        placeholder="Please specify your preferred style..."
                        className="appearance-none block w-full px-3 py-2 border border-tangerine/30 rounded-md placeholder-gray-400 focus:outline-none focus:ring-tangerine focus:border-tangerine dark:bg-[var(--off-white)] dark:text-[var(--text-primary)]"
                        value={surveyData.preferredStyleCustom}
                        onChange={(e) => setSurveyData(prev => ({ ...prev, preferredStyleCustom: e.target.value }))}
                      />
                    </div>
                  )}
                </div>
              </div>
            )}

            <div className="pt-4 flex gap-4">
              {step === 2 && (
                <button
                  type="button"
                  onClick={() => setStep(1)}
                  className="w-1/3 flex justify-center py-3 px-4 border border-[var(--border-light)] rounded-lg text-base font-medium text-[var(--text-secondary)] hover:bg-gray-50 dark:hover:bg-gray-800 transition-all focus:outline-none"
                >
                  Back
                </button>
              )}
              <button
                type="submit"
                disabled={loading}
                className={`${step === 2 ? 'w-2/3' : 'w-full'} flex justify-center py-3 px-4 border border-transparent rounded-lg shadow-md shadow-tangerine/20 text-base font-bold text-white bg-tangerine hover:bg-tangerine/90 transition-all hover:-translate-y-0.5 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-tangerine disabled:opacity-50`}
              >
                {loading ? 'Processing...' : (step === 1 && userData.role === 'client' ? 'Next' : 'Create Account')}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default RegisterPage;
